/**
 * Geometry Wars–style neon geometric icons for modules & goods.
 * Pure canvas data-URLs, no photo backgrounds.
 */

const iconCache = new Map();

const RARITY_GLOW = {
    common:    '#66aacc',
    uncommon:  '#44ff99',
    rare:      '#44ccff',
    epic:      '#cc66ff',
    legendary: '#ffcc44',
};

/**
 * @param {string} shape
 * @param {string} color hex
 * @param {string} rarity
 * @param {number} seed
 * @param {{ broken?: boolean }} [opts]
 */
export function getNeonIcon(shape, color = '#00d9ff', rarity = 'common', seed = 0, opts = {}) {
    const key = `neon|${shape}|${color}|${rarity}|${seed}|${opts.broken ? 1 : 0}`;
    if (iconCache.has(key)) return iconCache.get(key);

    const size = 96;
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d');
    const glow = opts.broken ? '#ff4466' : (RARITY_GLOW[rarity] || RARITY_GLOW.common);
    const fill = opts.broken ? '#663344' : color;

    // Transparent plate with neon border
    ctx.clearRect(0, 0, size, size);
    roundRect(ctx, 4, 4, size - 8, size - 8, 14);
    ctx.fillStyle = 'rgba(6, 12, 28, 0.55)';
    ctx.fill();
    ctx.strokeStyle = glow;
    ctx.lineWidth = 2.5;
    ctx.shadowColor = glow;
    ctx.shadowBlur = 10;
    roundRect(ctx, 4, 4, size - 8, size - 8, 14);
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.save();
    ctx.translate(size / 2, size / 2);
    ctx.shadowColor = fill;
    ctx.shadowBlur = 12;
    ctx.fillStyle = fill;
    ctx.strokeStyle = '#ffffffcc';
    ctx.lineWidth = 2;

    drawNeonShape(ctx, shape, fill, seed);

    ctx.restore();

    if (opts.broken) {
        // Crack / X overlay
        ctx.strokeStyle = '#ff3355';
        ctx.lineWidth = 3;
        ctx.shadowColor = '#ff0000';
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.moveTo(28, 28);
        ctx.lineTo(68, 68);
        ctx.moveTo(68, 28);
        ctx.lineTo(28, 68);
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.fillStyle = 'rgba(255,50,80,0.85)';
        ctx.font = 'bold 11px Orbitron, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('BROKEN', size / 2, size - 12);
    }

    // Rarity pip
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(size - 14, 14, 4, 0, Math.PI * 2);
    ctx.fill();

    const url = c.toDataURL('image/png');
    iconCache.set(key, url);
    return url;
}

function drawNeonShape(ctx, shape, color, seed) {
    const r = (seed * 9301 + 49297) % 233280 / 233280;

    switch (shape) {
        case 'weapon':
            // Arrow / dart like Geometry Wars
            neonArrow(ctx, 0, 0, 22, color);
            break;
        case 'weapon2':
            neonArrow(ctx, -10, 2, 14, color);
            neonArrow(ctx, 10, 2, 14, color);
            break;
        case 'shield':
            ctx.beginPath();
            ctx.moveTo(0, -20);
            for (let i = 0; i <= 6; i++) {
                const a = -Math.PI / 2 + (i / 6) * Math.PI * 2;
                const rad = i % 2 === 0 ? 20 : 14;
                ctx.lineTo(Math.cos(a) * rad, Math.sin(a) * rad);
            }
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            break;
        case 'engine':
            // Twin thruster chevrons
            ctx.beginPath();
            ctx.moveTo(-8, -16); ctx.lineTo(0, 18); ctx.lineTo(8, -16);
            ctx.closePath(); ctx.fill();
            ctx.fillStyle = '#ff8844';
            ctx.beginPath();
            ctx.moveTo(-5, 10); ctx.lineTo(0, 22); ctx.lineTo(5, 10);
            ctx.closePath(); ctx.fill();
            break;
        case 'reactor':
            ctx.beginPath(); ctx.arc(0, 0, 16, 0, Math.PI * 2); ctx.fill();
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 2;
            ctx.beginPath(); ctx.arc(0, 0, 9, 0, Math.PI * 2); ctx.stroke();
            ctx.beginPath(); ctx.arc(0, 0, 4, 0, Math.PI * 2); ctx.fill();
            break;
        case 'hull':
            // Hexagon
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
                const a = (i / 6) * Math.PI * 2 - Math.PI / 6;
                const px = Math.cos(a) * 18, py = Math.sin(a) * 18;
                if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
            }
            ctx.closePath(); ctx.fill(); ctx.stroke();
            break;
        case 'sensor':
            ctx.beginPath(); ctx.arc(0, 4, 6, 0, Math.PI * 2); ctx.fill();
            ctx.strokeStyle = color;
            ctx.lineWidth = 2.5;
            ctx.beginPath(); ctx.arc(0, 4, 12, Math.PI * 1.1, -0.1); ctx.stroke();
            ctx.beginPath(); ctx.arc(0, 4, 18, Math.PI * 1.15, -0.15); ctx.stroke();
            break;
        case 'cargo':
            ctx.fillRect(-14, -12, 28, 22);
            ctx.strokeRect(-14, -12, 28, 22);
            ctx.strokeStyle = '#fff6';
            ctx.strokeRect(-8, -6, 16, 10);
            break;
        case 'special':
            star(ctx, 0, 0, 5, 18, 8);
            ctx.fill(); ctx.stroke();
            break;
        case 'wing':
            ctx.beginPath();
            ctx.moveTo(2, -16); ctx.lineTo(20, 14); ctx.lineTo(2, 8); ctx.closePath(); ctx.fill();
            ctx.beginPath();
            ctx.moveTo(-2, -16); ctx.lineTo(-20, 14); ctx.lineTo(-2, 8); ctx.closePath(); ctx.fill();
            break;
        case 'crystal':
            ctx.beginPath();
            ctx.moveTo(0, -18); ctx.lineTo(12, 0); ctx.lineTo(0, 18); ctx.lineTo(-12, 0);
            ctx.closePath(); ctx.fill(); ctx.stroke();
            break;
        case 'fuel':
            ctx.fillRect(-8, -16, 16, 26);
            ctx.strokeRect(-8, -16, 16, 26);
            ctx.fillStyle = '#ff6622';
            ctx.beginPath(); ctx.arc(0, 4, 5, 0, Math.PI * 2); ctx.fill();
            break;
        case 'scrap':
            ctx.save(); ctx.rotate(0.4 + r);
            ctx.fillRect(-12, -5, 20, 10); ctx.restore();
            ctx.save(); ctx.rotate(-0.6);
            ctx.fillRect(-6, -8, 16, 8); ctx.restore();
            break;
        case 'chip':
            ctx.fillRect(-14, -14, 28, 28);
            ctx.strokeStyle = '#0af';
            for (let i = -2; i <= 2; i++) {
                ctx.fillRect(i * 5 - 1, -10, 2, 20);
            }
            break;
        case 'drone':
            neonArrow(ctx, 0, 0, 14, color);
            ctx.strokeStyle = color;
            ctx.beginPath(); ctx.moveTo(-16, 0); ctx.lineTo(-10, 0); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(10, 0); ctx.lineTo(16, 0); ctx.stroke();
            break;
        case 'armor':
            ctx.beginPath();
            ctx.moveTo(0, -16); ctx.lineTo(14, -4); ctx.lineTo(10, 16);
            ctx.lineTo(-10, 16); ctx.lineTo(-14, -4);
            ctx.closePath(); ctx.fill(); ctx.stroke();
            break;
        case 'ship':
            neonArrow(ctx, 0, 0, 26, color);
            break;
        default:
            neonArrow(ctx, 0, 0, 16, color);
    }
}

function neonArrow(ctx, x, y, s, color) {
    // Geometry Wars dart: pointed triangle with trailing V
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(x, y - s);
    ctx.lineTo(x + s * 0.72, y + s * 0.55);
    ctx.lineTo(x, y + s * 0.22);
    ctx.lineTo(x - s * 0.72, y + s * 0.55);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#ffffffaa';
    ctx.lineWidth = 1.8;
    ctx.stroke();
    // core glow
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = 0.85;
    ctx.beginPath();
    ctx.moveTo(x, y - s * 0.45);
    ctx.lineTo(x + s * 0.18, y + s * 0.05);
    ctx.lineTo(x - s * 0.18, y + s * 0.05);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
}

function star(ctx, cx, cy, spikes, outer, inner) {
    let rot = -Math.PI / 2;
    const step = Math.PI / spikes;
    ctx.beginPath();
    for (let i = 0; i < spikes; i++) {
        ctx.lineTo(cx + Math.cos(rot) * outer, cy + Math.sin(rot) * outer);
        rot += step;
        ctx.lineTo(cx + Math.cos(rot) * inner, cy + Math.sin(rot) * inner);
        rot += step;
    }
    ctx.closePath();
}

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

/** Large neon ship icon for hangar center */
export function getNeonShipPreview(color = '#00d9ff', size = 200) {
    const key = `shipprev|${color}|${size}`;
    if (iconCache.has(key)) return iconCache.get(key);
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, size, size);
    // soft glow
    const g = ctx.createRadialGradient(size / 2, size / 2, 10, size / 2, size / 2, size * 0.45);
    g.addColorStop(0, color + '55');
    g.addColorStop(1, 'transparent');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size * 0.42, 0, Math.PI * 2);
    ctx.fill();

    ctx.save();
    ctx.translate(size / 2, size / 2 + 6);
    ctx.shadowColor = color;
    ctx.shadowBlur = 22;
    neonArrow(ctx, 0, 0, size * 0.28, color);
    // second wing layer
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = '#aa66ff';
    ctx.shadowColor = '#aa66ff';
    ctx.beginPath();
    ctx.moveTo(0, size * 0.08);
    ctx.lineTo(size * 0.14, size * 0.2);
    ctx.lineTo(0, size * 0.12);
    ctx.lineTo(-size * 0.14, size * 0.2);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    const url = c.toDataURL('image/png');
    iconCache.set(key, url);
    return url;
}
